import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { DataFormPage } from "~/pages/DataFormPage";
import { queryClient, trpc } from "~/utils/trpc";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@quake/shared-ui/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@quake/shared-ui/components/ui/card";
import { toast } from "@quake/shared-ui/lib/sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@quake/shared-ui/components/ui/dialog";
import { useState } from "react";
import TrashIcon from "lucide-react/dist/esm/icons/trash";

export const Route = createFileRoute("/_dashboard/$datasetSlug/data/custom/details/$id/settings")({
  component: RouteComponent,
  loader: async ({ params }) => {
    const entity = await queryClient.fetchQuery(
      trpc.customEntities.getCustomEntitiesById.queryOptions({ id: params.id }),
    );
    return { entity };
  },
});

function RouteComponent() {
  const { id, datasetSlug } = Route.useParams();
  const { entity } = Route.useLoaderData();
  const [deleteEntityDialogOpen, setDeleteEntityDialogOpen] = useState(false);
  const navigate = useNavigate();
  const queryClientInstance = useQueryClient();

  useBreadCrumbs([
    {
      title: entity?.p_name || entity?.name || "Custom",
      link: `/${datasetSlug}/data/custom/details/${id}/table`,
    },
    { title: "Settings", link: `/${datasetSlug}/data/custom/details/${id}/settings` },
  ]);

  const deleteEntity = useMutation(
    trpc.customEntities.deleteEntity.mutationOptions({
      onSuccess: async (res) => {
        await queryClientInstance.invalidateQueries({
          queryKey: trpc.customEntities.getCustomModels.queryKey(),
        });
        navigate({ to: `/${datasetSlug}/data/jobs` });
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  return (
    <div className="mx-5 pb-8">
      <h1 className="text-2xl font-bold mb-6">{entity?.p_name || entity?.name} Settings</h1>

      <div className="space-y-6">
        <div className="[&_.max-w-2xl]:max-w-full! [&_.mx-auto]:mx-0! [&>div]:m-0!">
          <DataFormPage
            entity={entity}
            hideHeader={false}
            customTitle="Entity Configuration"
            customDescription="Configure the basic properties and attributes for this entity."
          />
        </div>

        <div>
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive">Danger Zone</CardTitle>
              <CardDescription>
                Permanently delete this entity and all its data. This action cannot be undone.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="destructive" onClick={() => setDeleteEntityDialogOpen(true)}>
                <TrashIcon className="mr-2 h-4 w-4" />
                Delete {entity?.name} Entity and its data
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={deleteEntityDialogOpen} onOpenChange={setDeleteEntityDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Delete This Entity</DialogTitle>
            <DialogDescription>
              Are you sure you want to <b>delete</b> this entity with its data? This action <b>cannot be undone.</b>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteEntityDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                deleteEntity.mutate({ id: String(entity?.id) });
              }}
              variant="destructive"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
